import React from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import styled from 'styled-components/native'
import { Colors } from '../../constants/colors'
import { icons } from '../../constants/searchAssets'
import {
  ATLDivider,
  ATLSingleImage,
  ATLSpacing,
  ImagePickerTypeEnum,
  SpacingSizesEnum,
} from '@appsimples/atlantica-react-native'
import { atlTheme } from '../../atlTheme'
import { CustomATLIconizedText } from './components/CustomATLIconizedText'
import { PercentageBar } from './components/PercentageBar'
import { IconAndBarComponent } from './components/IconAndBarComponent'
import { View } from 'react-native'
import { TextTwoColors } from '../../components/TextTwoColors'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { Loading } from '../../components/loading/Loading'
import { formatDateWithMonthAndYear } from '../../utils/formatters'
import { ConstructionModuleSelector } from '../../components/ConstructionModuleSelector'
import { CardContainer } from './components/GenericComponents'
import { useConstructionData } from '../../utils/hooks/useConstructionData'
import { TouchableOpacity } from 'react-native'
import { openWhatsApp } from '../../utils/openWhatsApp'
import { BRIO_SAC_WHATSAPP_NUMBER, whatsAppTextContactUs } from '../../constants/whatsAppInfo'
import { getAddressToDisplay } from './utils/getAddress'
import { platformConfig } from '../../constants/platform'
import { ProgressBarTitle } from './components/ProgressBarTitle'
import { CustomCarousel } from '../../components/customCarousel/CustomCarousel'
import { screenHeight } from '../../constants/dimensions'

const MIN_CAROUSEL_HEIGHT = 180

const BackgroundContainer = styled.ScrollView`
  background-color: ${Colors.backgroundGrey};
  height: 100%;
`

const WhiteCardEnterprise = styled.View`
  background-color: ${Colors.white};
  margin-vertical: ${atlTheme.SpacingAtomic}px;
  border-bottom-right-radius: ${atlTheme.BorderRadiusDateIndicator}px;
  border-bottom-left-radius: ${atlTheme.BorderRadiusDateIndicator}px;
`

const WhiteCardPercentageBar = styled.View`
  background-color: ${Colors.white};
  margin-vertical: ${atlTheme.SpacingAtomic}px;
  border-radius: ${atlTheme.BorderRadiusDateIndicator}px;
  padding-top: ${atlTheme.SpacingDistribute}px;
  padding-bottom: ${atlTheme.SpacingMarginal}px;
`

const CalendarIconContainer = styled.Image`
  margin-left: ${atlTheme.SpacingMarginal}px;
  margin-right: ${atlTheme.SpacingAtomic}px;
`

const DateInfoContainer = styled.View`
  margin: 0 ${atlTheme.SpacingMarginal}px;
`

const TotalConstructionStatusContainer = styled.View`
  flex-direction: row;
  margin-bottom: ${atlTheme.SpacingMarginal}px;
`

const ContactUsLabel = styled.Text`
  font-family: ${atlTheme.FontFamilyBrand};
  font-weight: ${atlTheme.FontWeight500};
  font-size: ${atlTheme.FontSizeAuxiliary}px;
  line-height: ${atlTheme.LineHeightHeading}px;
  text-transform: uppercase;
  color: ${atlTheme.ColorMediumContrast};
  font-size: ${atlTheme.FontSizeBody}px;
`

const ContactUsContainer = styled.View`
  display: flex;
  align-items: center;
  background-color: ${Colors.white};
  margin-top: ${atlTheme.SpacingAtomic}px;
`

const ContactUsContainerButton = styled.View`
  display: flex;
  flex-direction: row;
  align-self: center;
  margin: ${atlTheme.SpacingBase}px 0;
`

const ContactUsActionLabel = styled.Text`
  font-family: ${atlTheme.FontFamilyBrand};
  font-weight: ${atlTheme.FontWeight500};
  font-size: ${atlTheme.FontSizeAuxiliary}px;
  line-height: ${atlTheme.LineHeightHeading}px;
  text-transform: uppercase;
  color: ${atlTheme.ColorBrand2Default};
  font-size: ${atlTheme.FontSizeBody}px;
  margin-left: ${atlTheme.SpacingAtomic}px;
`
export const ConstructionProgressScreen = ({}) => {
  const { constructionData, isFetchingConstructionData } = useConstructionData()

  const addressToDisplay = getAddressToDisplay(constructionData?.construction?.constructionSpecificAddress)

  const constructionImages = constructionData?.construction?.dashboardMedia.map(media => {
    return {
      id: media.id,
      image: {
        uri: media.url,
      },
    }
  })

  const carouselHeight = Math.max(screenHeight / 4, MIN_CAROUSEL_HEIGHT) - 2 * atlTheme.SpacingMarginal

  const onPressContactUs = () => {
    openWhatsApp(whatsAppTextContactUs, BRIO_SAC_WHATSAPP_NUMBER)
  }

  return (
    <BackgroundContainer>
      {isFetchingConstructionData ? (
        <>
          <ATLSpacing type={SpacingSizesEnum.marginal} />
          <Loading />
        </>
      ) : !constructionData ? (
        <ContactUsContainer>
          <ContactUsLabel>{formatMessage(messages.error_userNotHaveConstruction)}</ContactUsLabel>
          <ContactUsContainerButton>
            <ContactUsLabel>{formatMessage(messages.constructions_contact_title)}</ContactUsLabel>
            <TouchableOpacity onPress={onPressContactUs}>
              <ContactUsActionLabel>{formatMessage(messages.constructions_contact_button)}</ContactUsActionLabel>
            </TouchableOpacity>
          </ContactUsContainerButton>
        </ContactUsContainer>
      ) : (
        <>
          <CardContainer>
            <ConstructionModuleSelector />
          </CardContainer>
          <WhiteCardEnterprise>
            <CustomCarousel images={constructionImages} carouselHeight={carouselHeight} />
            <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} />
            <CustomATLIconizedText icon={icons.towerIcon} text={constructionData?.constructionUnit?.name} />
            <CustomATLIconizedText icon={icons.locationIcon} text={addressToDisplay} />
          </WhiteCardEnterprise>
          <WhiteCardPercentageBar>
            <TotalConstructionStatusContainer>
              <CalendarIconContainer source={icons.calendarIcon} />
              <View>
                <TextTwoColors
                  firstText={formatMessage(messages.constructionModule_workBegin)}
                  secondText={formatDateWithMonthAndYear(new Date(constructionData?.construction?.startDate))}
                />
                <TextTwoColors
                  firstText={formatMessage(messages.constructionModule_finishForecast)}
                  secondText={formatDateWithMonthAndYear(new Date(constructionData?.construction?.endDate))}
                />
              </View>
            </TotalConstructionStatusContainer>
            <DateInfoContainer>
              <ProgressBarTitle
                title={formatMessage(messages.constructionModule_totalWork)}
                percentage={constructionData?.construction?.generalProgress}
              />
            </DateInfoContainer>
            <PercentageBar
              percentageDone={constructionData?.construction?.generalProgress}
              height={platformConfig.isWeb() ? atlTheme.SpacingDistribute : atlTheme.SpacingMarginal}
              marginBottom={atlTheme.SpacingBase}
            />
            <IconAndBarComponent
              title={formatMessage(messages.constructionModule_foundation)}
              percentage={constructionData?.construction?.foundationProgress || 0}
              icon={icons.floorPlanIcon}
            />
            <IconAndBarComponent
              percentage={constructionData?.construction?.structureProgress || 0}
              icon={icons.wallIcon}
              title={formatMessage(messages.constructionModule_structure)}
            />
            <IconAndBarComponent
              icon={icons.formatPaintIcon}
              title={formatMessage(messages.constructionModule_finishing)}
              percentage={constructionData?.construction?.complementsProgress || 0}
            />
          </WhiteCardPercentageBar>
        </>
      )}
    </BackgroundContainer>
  )
}
